import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { PUSH_NAVIGATE_MESSAGE } from './fcm-sw.js';
import type { PushClient, PushForegroundMessage } from './push-client.js';
import { derivePushState, readPushEnvironment, type PushState } from './push-state.js';

/**
 * App-wide push state, ONE instance per app.
 *
 * Mount once, inside the router (it navigates) and inside auth (it needs the
 * uid). Everything that shows push — the settings control, the contextual
 * "we'll tell you when your car is ready" prompt, the foreground toast — reads
 * `usePushRegistration()`, so they agree on state and only one foreground
 * listener and one boot-time token refresh exist.
 *
 * Lifecycle it owns:
 *   - permission state, re-read when the tab regains focus (the person may
 *     have flipped it in browser settings meanwhile);
 *   - on every signed-in boot with permission granted: mint the token and
 *     upsert `fcmTokens/{token}` with a fresh `lastUsedAt`;
 *   - foreground messages → `latestMessage` (the toast renders it);
 *   - worker `kilat-push-navigate` messages (a notification tapped while a
 *     window was open) → `onNavigate`.
 * Sign-out cleanup is NOT here: it must run before auth switches users, so the
 * app's sign-out calls `client.cleanupForSignOut(uid)` directly.
 */

export interface PushRegistration {
  readonly state: PushState;
  /** True while the permission prompt / token registration is in flight. */
  readonly busy: boolean;
  /** Set when permission was granted but registering this device failed. */
  readonly registrationFailed: boolean;
  /** Prompt + register. Resolves to the resulting state. */
  readonly enable: () => Promise<PushState>;
  readonly latestMessage: PushForegroundMessage | null;
  readonly dismissLatest: () => void;
}

const PushContext = createContext<PushRegistration | null>(null);

const INERT: PushRegistration = {
  state: 'unsupported',
  busy: false,
  registrationFailed: false,
  enable: async () => 'unsupported',
  latestMessage: null,
  dismissLatest: () => undefined,
};

export interface PushProviderProps {
  readonly client: PushClient;
  /** Signed-in uid, or null. Token registration only happens with a uid. */
  readonly uid: string | null;
  /** Route to an absolute in-app URL (a tapped notification, a toast tap). */
  readonly onNavigate: (href: string) => void;
  readonly children: ReactNode;
}

export function PushProvider({ client, uid, onNavigate, children }: PushProviderProps) {
  const [supported, setSupported] = useState<boolean | null>(null);
  const [state, setState] = useState<PushState>('checking');
  const [busy, setBusy] = useState(false);
  const [registrationFailed, setRegistrationFailed] = useState(false);
  const [latestMessage, setLatestMessage] = useState<PushForegroundMessage | null>(null);
  const navigateRef = useRef(onNavigate);
  navigateRef.current = onNavigate;

  // Support + permission, re-read on focus/visibility.
  useEffect(() => {
    let cancelled = false;
    void client.isSupported().then((ok) => {
      if (cancelled) return;
      setSupported(ok);
      setState(derivePushState(readPushEnvironment(ok)));
    });
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    if (supported === null) return undefined;
    const reread = () => setState(derivePushState(readPushEnvironment(supported)));
    window.addEventListener('focus', reread);
    document.addEventListener('visibilitychange', reread);
    return () => {
      window.removeEventListener('focus', reread);
      document.removeEventListener('visibilitychange', reread);
    };
  }, [supported]);

  // Signed-in boot with permission already granted: keep the token fresh.
  const refreshedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!uid || state !== 'on' || refreshedFor.current === uid) return;
    refreshedFor.current = uid;
    void client.refresh(uid).then((ok) => setRegistrationFailed(!ok));
  }, [client, uid, state]);

  // Foreground messages.
  useEffect(() => {
    if (!supported) return undefined;
    let unsub: (() => void) | null = null;
    let cancelled = false;
    void client.config.messaging
      .onForegroundMessage((msg) => setLatestMessage(msg))
      .then((u) => {
        if (cancelled) u();
        else unsub = u;
      })
      .catch((e: unknown) =>
        client.config.logger.warn('push: foreground listener failed', { error: String(e) }),
      );
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [client, supported]);

  // A notification tapped while this window was open.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
    const onMessage = (event: MessageEvent<unknown>) => {
      const data = event.data;
      if (
        typeof data === 'object' &&
        data !== null &&
        'type' in data &&
        data.type === PUSH_NAVIGATE_MESSAGE &&
        'url' in data &&
        typeof data.url === 'string'
      ) {
        navigateRef.current(data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  const enable = useCallback(async (): Promise<PushState> => {
    if (!uid || !supported) return state;
    setBusy(true);
    try {
      await client.enable(uid);
      setRegistrationFailed(false);
      refreshedFor.current = uid;
    } catch {
      setRegistrationFailed(true);
    } finally {
      setBusy(false);
    }
    const next = derivePushState(readPushEnvironment(supported));
    setState(next);
    return next;
  }, [client, uid, supported, state]);

  const dismissLatest = useCallback(() => setLatestMessage(null), []);

  const value = useMemo<PushRegistration>(
    () => ({ state, busy, registrationFailed, enable, latestMessage, dismissLatest }),
    [state, busy, registrationFailed, enable, latestMessage, dismissLatest],
  );

  return <PushContext.Provider value={value}>{children}</PushContext.Provider>;
}

/** Push state for the current app. Outside a PushProvider it reports `unsupported`. */
export function usePushRegistration(): PushRegistration {
  return useContext(PushContext) ?? INERT;
}
